import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import dayjs from 'dayjs'

import ClockCircleOutlinedIcon from '@ant-design/icons/ClockCircleOutlined'
import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'
import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Drawer from '@mui/material/Drawer'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import IconButton from '@components/_mui/IconButton'

import { useMe } from '@shared/providers/src/MeProvider'
import { assessmentTypeToLabel, AvailabilityType, mapRailsTimezoneToJS, UserRole } from '@shared/utils'

import AssessmentDetailsModal from '@pages/Assessments/AssessmentDetailsModal'
import ProviderCBOsDetailsModal from '@pages/CBOs/ProviderCBOsDetailsModal'
import AssessmentsForReview from '@pages/Schedule/AssessmentsForReview'
import LabsForReview from '@pages/Schedule/LabsForReview'
import Avatar from '@components/Avatar'
import CalendarDate from '@components/CalendarDate'
import MainCard from '@components/MainCard'
import CBO from '@components/TableCell/CBO'
import Patient from '@components/TableCell/Patient'
import Provider from '@components/TableCell/Provider'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'

import { useScheduledType, useScheduledUsersType, useUnassignedQHALifestyleMeds } from './Schedule.hooks'

export default function ScheduleDrawer({ open, onClose }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      sx={{ zIndex: 1300 }}
      slotProps={{
        paper: {
          sx: styles.drawer,
        },
      }}
      data-testid="drawer-schedule"
    >
      <MainCard
        title="Schedule"
        secondary={
          <IconButton shape="rounded" color="inherit" onClick={onClose} data-testid="close-schedule-drawer">
            <CloseOutlinedIcon />
          </IconButton>
        }
        sx={styles.card}
        content={false}
        data-testid="schedule-drawer"
      >
        <Content />
      </MainCard>
    </Drawer>
  )
}

const ROLE_OPTIONS = [
  { value: UserRole.Provider, label: 'Providers' },
  { value: UserRole.MA, label: 'PSRs' },
  { value: UserRole.Support, label: 'Support' },
]

function Content() {
  const me = useMe()
  const isMA = me.role === UserRole.MA

  const [date, setDate] = useState(dayjs())
  const [role, setRole] = useState(UserRole.Provider)
  const [tab, setTab] = useState('on')

  const isProviders = role === UserRole.Provider

  const handleRoleChange = (value) => {
    if (!value || value === role) return
    setRole(value)
    if (value !== UserRole.Provider && tab === 'lifestyleMeds') setTab('on')
  }

  return (
    <Stack sx={styles.content}>
      <Box sx={styles.header}>
        <CalendarDate hideToday view="day" date={date} onDateChange={setDate} />
        <ToggleButtonGroup
          fullWidth
          exclusive
          size="small"
          color="primary"
          value={role}
          onChange={(e, value) => handleRoleChange(value)}
          aria-label="Schedule Role"
          sx={{ my: 1 }}
        >
          {ROLE_OPTIONS.map(({ value, label }) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tabs variant="fullWidth" value={tab} onChange={(e, tab) => setTab(tab)}>
          <Tab label="Scheduled On" value="on" />
          <Tab label="Scheduled Off" value="off" />
          <Tab label="Out of Office" value="ooo" />
          {isProviders && !isMA && <Tab label="Lifestyle Meds" value="lifestyleMeds" />}
        </Tabs>
      </Box>

      {isProviders ? <ProvidersContent tab={tab} date={date} /> : <UsersContent tab={tab} role={role} date={date} />}
    </Stack>
  )
}

function ProvidersContent({ tab, date }) {
  const isLifestyleMeds = tab === 'lifestyleMeds'
  const scheduledQuery = useScheduledType(isLifestyleMeds ? 'on' : tab, date)

  return (
    <>
      {tab === 'on' && (
        <ScheduleSection
          title="Scheduled On"
          query={scheduledQuery}
          emptyMessage="No providers scheduled on for this day"
          renderItem={(provider) => <ProviderItem key={provider.id} data={provider} />}
        />
      )}
      {tab === 'off' && (
        <ScheduleSection
          title="Scheduled Off"
          query={scheduledQuery}
          emptyMessage="No providers scheduled off for this day"
          renderItem={(provider) => <ProviderItem key={provider.id} data={provider} />}
        />
      )}
      {tab === 'ooo' && (
        <ScheduleSection
          title="Out of Office"
          query={scheduledQuery}
          emptyMessage="No providers out of office for this day"
          renderItem={(provider) => <ProviderItem key={provider.id} showCBOs data={provider} />}
        />
      )}
      {isLifestyleMeds && <LifestyleMedsSection />}
    </>
  )
}

function UsersContent({ tab, role, date }) {
  const scheduledQuery = useScheduledUsersType(tab, role, date)
  const roleLabel = role === UserRole.MA ? 'PSRs' : 'support users'

  return (
    <>
      {tab === 'on' && (
        <ScheduleSection
          title="Scheduled On"
          query={scheduledQuery}
          emptyMessage={`No ${roleLabel} scheduled on for this day`}
          renderItem={(user) => <UserItem key={user.id} data={user} />}
        />
      )}
      {tab === 'off' && (
        <ScheduleSection
          title="Scheduled Off"
          query={scheduledQuery}
          emptyMessage={`No ${roleLabel} scheduled off for this day`}
          renderItem={(user) => <UserItem key={user.id} data={user} />}
        />
      )}
      {tab === 'ooo' && (
        <ScheduleSection
          title="Out of Office"
          query={scheduledQuery}
          emptyMessage={`No ${roleLabel} out of office for this day`}
          renderItem={(user) => <UserItem key={user.id} data={user} />}
        />
      )}
    </>
  )
}

function ScheduleSection({ title, query, emptyMessage, renderItem }) {
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = query

  if (isPending) return <ScheduleSkeleton />

  return (
    <Stack spacing={1} sx={styles.section}>
      <Typography variant="h5" sx={{ px: 1 }}>
        {title}
      </Typography>

      {data?.length > 0 ? (
        <Stack spacing={1}>
          {data.map(renderItem)}
          {hasNextPage && (
            <Button
              fullWidth
              loading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              endIcon={<MoreOutlinedIcon rotate={90} />}
              loadingPosition="end"
              sx={{ textTransform: 'none' }}
            >
              more
            </Button>
          )}
        </Stack>
      ) : (
        <Stack sx={styles.emptyState}>
          <Typography sx={{ color: 'text.secondary' }}>{emptyMessage}</Typography>
        </Stack>
      )}
    </Stack>
  )
}

function processSlots(slots, type, tz) {
  return (slots || []).map((slot) => {
    const start = tz ? dayjs(slot.start).tz(tz) : dayjs(slot.start)
    const end = tz ? dayjs(slot.end).tz(tz) : dayjs(slot.end)
    return { type, timestamp: start.valueOf(), label: `${start.format('LT')} - ${end.format('LT')}` }
  })
}

function ProviderItem({ showCBOs, data }) {
  const me = useMe()
  const isMA = me.role === UserRole.MA

  const [CBOsOpen, setCBOsOpen] = useState(false)
  const [labsOpen, setLabsOpen] = useState(false)
  const [assessmentsOpen, setAssessmentsOpen] = useState(false)

  const tz = data.timezone ? mapRailsTimezoneToJS(data.timezone) : null
  const showLabs = !isMA && data.needsReviewLabs?.length > 0
  const showAssessments = data.pendingAssessments?.length > 0

  const items = useMemo(() => {
    const availabilities = processSlots(data.availabilityTimeRanges, AvailabilityType.Appointment, tz)
    const adminTimes = processSlots(data.adminTimeRanges, AvailabilityType.Admin, tz)
    const outOfOfficeTimes = processSlots(data.outOfOfficeTimeRanges, AvailabilityType.OutOfOffice, tz)

    return availabilities.concat(adminTimes, outOfOfficeTimes).sort((a, b) => a.timestamp - b.timestamp)
  }, [data.adminTimeRanges, data.availabilityTimeRanges, data.outOfOfficeTimeRanges, tz])

  return (
    <Box key={data.id} sx={styles.providerCard}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Provider user={data} />
        <Stack direction="row" spacing={0.5}>
          {showCBOs && (
            <>
              <Button variant="outlined" onClick={() => setCBOsOpen(true)}>
                CBOs
              </Button>
              <ProviderCBOsDetailsModal provider={data} open={CBOsOpen} onClose={() => setCBOsOpen(false)} />
            </>
          )}
          {showLabs && (
            <>
              <Button variant="contained" onClick={() => setLabsOpen(true)}>
                Labs
              </Button>
              <LabsForReview provider={data} labs={data.needsReviewLabs} open={labsOpen} onClose={() => setLabsOpen(false)} />
            </>
          )}
          {showAssessments && (
            <>
              <Button variant="contained" onClick={() => setAssessmentsOpen(true)}>
                Asmts
              </Button>
              <AssessmentsForReview
                provider={data}
                assessments={data.pendingAssessments}
                open={assessmentsOpen}
                onClose={() => setAssessmentsOpen(false)}
              />
            </>
          )}
        </Stack>
      </Stack>
      <ScheduledHours tz={tz} items={items} />
    </Box>
  )
}

function UserItem({ data }) {
  // BE interprets schedule times for timezone-less users as Eastern — mirror that fallback
  const tz = mapRailsTimezoneToJS(data.timezone || 'Eastern Time (US & Canada)')

  const items = useMemo(() => {
    const adminTimes = processSlots(data.adminTimeRanges, AvailabilityType.Admin, tz)
    const outOfOfficeTimes = processSlots(data.outOfOfficeTimeRanges, AvailabilityType.OutOfOffice, tz)

    return adminTimes.concat(outOfOfficeTimes).sort((a, b) => a.timestamp - b.timestamp)
  }, [data.adminTimeRanges, data.outOfOfficeTimeRanges, tz])

  return (
    <Box sx={styles.providerCard}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Avatar hover="card" user={data} />
        <Typography>{data.fullName}</Typography>
      </Stack>
      <ScheduledHours tz={tz} items={items} />
    </Box>
  )
}

function ScheduledHours({ tz, items }) {
  if (!items.length) return null

  return (
    <Stack spacing={1} sx={styles.scheduleContainer}>
      <Stack direction="row" sx={styles.scheduleTitle}>
        <ClockCircleOutlinedIcon />
        <Typography variant="body2">Scheduled Hours {tz ? `| ${dayjs().tz(tz).format('z')}` : ''}</Typography>
      </Stack>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        {items.map((slot, index) => (
          <Chip
            key={index}
            color={slot.type === AvailabilityType.Appointment ? 'info' : slot.type === AvailabilityType.Admin ? 'warning' : 'secondary'}
            variant="outlined"
            label={slot.label}
            size="small"
          />
        ))}
      </Stack>
    </Stack>
  )
}

function LifestyleMedsSection() {
  const [isAsmtDetailsOpen, setIsAsmtDetailsOpen] = useState(false)
  const [selectedAsmtId, setSelectedAsmtId] = useState(null)

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useUnassignedQHALifestyleMeds()

  const showLoading = isPending
  const showEmpty = !isPending && !data?.length
  const showData = !isPending && data?.length > 0

  return (
    <Stack spacing={1} sx={styles.section}>
      <Typography variant="h5" sx={{ px: 1 }}>
        Lifestyle Meds QHAs
      </Typography>

      <AssessmentDetailsModal
        canMarkExceptional
        align="flex-start"
        open={isAsmtDetailsOpen}
        assessmentId={selectedAsmtId}
        onClose={() => setIsAsmtDetailsOpen(false)}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>CBO</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Date Returned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoading && <TableLoader columns={4} double items={5} />}
            {showEmpty && <TableEmpty columns={4} title="No unassigned Lifestyle Meds QHAs" />}
            {showData &&
              data.map((assessment) => (
                <TableRow
                  hover
                  key={assessment.id}
                  onClick={() => {
                    setSelectedAsmtId(assessment.id)
                    setIsAsmtDetailsOpen(true)
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Patient small user={assessment.user}>
                      <Typography variant="body2">
                        MRN:
                        <Link
                          component={RouterLink}
                          to={`/app/patients/${assessment.user?.id}`}
                          target="_blank"
                          rel="noopener"
                          sx={{ ml: 1 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {assessment.user?.id}
                        </Link>
                      </Typography>
                    </Patient>
                  </TableCell>
                  <TableCell>
                    <CBO small data={assessment.user?.cbo} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{assessmentTypeToLabel[assessment.assessmentType]}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{assessment.dateReturned ? dayjs(assessment.dateReturned).format('L') : '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasNextPage && (
        <Button
          fullWidth
          loading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          endIcon={<MoreOutlinedIcon rotate={90} />}
          loadingPosition="end"
          sx={{ textTransform: 'none' }}
        >
          more
        </Button>
      )}
    </Stack>
  )
}

function ScheduleSkeleton() {
  return (
    <Stack spacing={1} sx={styles.section}>
      <Typography variant="h5" sx={{ px: 1 }}>
        <Skeleton width={120} />
      </Typography>

      {[1, 2, 3].map((item) => (
        <Box key={item} sx={styles.providerCard}>
          <Provider.Loading />

          <Stack spacing={1} sx={styles.scheduleContainer}>
            <Stack direction="row" sx={styles.scheduleTitle}>
              <ClockCircleOutlinedIcon />
              <Typography variant="body2">Scheduled Hours</Typography>
            </Stack>
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
              {[1, 2].map((slot) => (
                <Skeleton key={slot} variant="rounded" width={80} height={24} />
              ))}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

const styles = {
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    width: 800,
  },
  card: {
    border: 'none',
    borderRadius: 0,
    height: '100vh',
    '& .MuiCardHeader-root': {
      color: 'primary.contrastText',
      backgroundColor: 'primary.main',
      '& .MuiTypography-root': { fontSize: '1rem' },
    },
  },
  content: {
    height: '100%',
    backgroundColor: 'background.default',
    overflowY: 'auto',
    flexDirection: 'column',
    pb: 10,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'background.paper',
    pt: 1,
    px: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  section: {
    p: 2,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1,
    backgroundColor: 'action.hover',
    py: 4,
  },
  providerCard: {
    p: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
  },
  scheduleContainer: {
    borderTop: '1px dashed',
    borderColor: 'divider',
    mt: 1,
    pt: 1,
  },
  scheduleTitle: {
    gap: 1,
    alignItems: 'center',
    color: 'text.secondary',
  },
}
